import { useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatch } from "react-router";
import { listWorkflows, type WorkflowSummary } from "../../../api/content/workflowsApi";
import { appPaths, ROUTE_PATTERNS } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import { classNames } from "../../../ui/styles/classNames";
import Chip from "../../../ui/feedback/Chip";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import EmptyState from "../../../ui/feedback/EmptyState";
import WorkflowGroupList from "../list/WorkflowGroupList";

/**
 * The project's Workflows at its latest model version (FR-WF-1), in the Workflows
 * section's sidebar, the one place to browse them (F36, SB-19), grouped as every Workflow
 * list is (`WorkflowGroupList`, F97, F111): searched there, each with its name, what it does
 * and how many steps it has, and its star. Each opens in the Workflow view.
 */
export default function WorkflowLibrary({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  // The sidebar sits outside the page's routes, so it reads the open Workflow from the address (Sol S1).
  const workflowAddress = useMatch(ROUTE_PATTERNS.workflow)?.params.workflowAddress ?? null;
  const load = useCallback((signal: AbortSignal) => listWorkflows(projectId, null, signal), [projectId]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("workflows.library.loading")} loadingClassName="px-4 py-3" onRetry={reload} compact={true} />;
  return (
    <div className="space-y-2 px-2 py-2">
      {state.value.workflows.length === 0 ? (
        <EmptyState className="px-2">{t("workflows.library.empty")}</EmptyState>
      ) : (
        <WorkflowGroupList
          projectId={projectId}
          workflows={state.value.workflows}
          current={workflowAddress}
          row={(workflow, marks) => <WorkflowRow key={workflow.reference} workflow={workflow} current={workflowAddress === workflow.reference} marks={marks} />}
        />
      )}
    </div>
  );
}

/** One Workflow to open, its marks on their own line below the link, so the star is never inside it
 * and a long "Part of …" never squeezes the name in the narrow sidebar (Sol S4). */
function WorkflowRow({ workflow, current, marks }: { workflow: WorkflowSummary; current: boolean; marks: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className={classNames("app-card space-y-1 p-2 hover:shadow", current ? "app-nav-item-active" : "")}>
      <Link to={appPaths.workflow(workflow.reference)} aria-current={current ? "page" : undefined} className="block space-y-1">
        <span className="flex items-start gap-2">
          <span className="app-title min-w-0 flex-1 text-sm font-semibold">{workflow.name}</span>
          <Chip size="compact" className="shrink-0">{t("workflows.node.steps", { count: workflow.stepCount })}</Chip>
        </span>
        {workflow.description === "" ? null : (
          // Two lines, the whole text on hover.
          <span className="app-text-muted line-clamp-2 block text-xs" title={workflow.description}>{workflow.description}</span>
        )}
      </Link>
      <span className="flex min-w-0 flex-wrap items-center gap-1">{marks}</span>
    </div>
  );
}
